import React, { useEffect, useRef } from "react";

// Optimized star canvas for smaller dashboard hero card
const StarCanvas = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const starsRef = useRef([]);
  const shootingStarsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    // Create balanced star field for smaller area
    const createStars = () => {
      const stars = [];
      // Balanced density - not too dense, with empty space
      const starCount = Math.floor((width * height) / 4000);

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 1.5 + 0.3,
          baseOpacity: Math.random() * 0.6 + 0.3,
          twinkleSpeed: Math.random() * 0.002 + 0.001,
          twinkleOffset: Math.random() * Math.PI * 2,
          // More stars twinkle
          twinkleChance: Math.random(),
          // Some stars have purple/violet tint
          color: Math.random() > 0.7
            ? `rgba(167, 139, 250, ${Math.random() * 0.5 + 0.5})`
            : `rgba(255, 255, 255, 1)`,
        });
      }
      return stars;
    };

    // Create a shooting star
    const createShootingStar = () => {
      const angle = Math.PI / 6 + Math.random() * Math.PI / 6;
      const startX = Math.random() * width * 0.6;
      const startY = Math.random() * height * 0.4;

      return {
        x: startX,
        y: startY,
        speed: 3 + Math.random() * 3,
        length: 40 + Math.random() * 40,
        opacity: 1,
        angle: angle,
        // Purple-tinted shooting stars
        color: Math.random() > 0.5 ? "#a78bfa" : "#c4b5fd",
      };
    };

    starsRef.current = createStars();

    // Start with a shooting star
    shootingStarsRef.current = [createShootingStar()];
    let lastShootingStarTime = 0;
    // Much more frequent: 2-5 seconds between shooting stars
    let shootingStarInterval = 2000 + Math.random() * 3000;

    const animate = (time) => {
      ctx.clearRect(0, 0, width, height);

      // Draw stars with twinkling
      starsRef.current.forEach((star) => {
        let opacity = star.baseOpacity;

        // 50% of stars twinkle for more life
        if (star.twinkleChance > 0.5) {
          const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
          opacity = star.baseOpacity * (0.5 + twinkle * 0.5);
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);

        // Apply star color with opacity
        if (star.color.includes("167")) {
          // Purple star
          ctx.fillStyle = `rgba(167, 139, 250, ${opacity})`;
        } else {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        }
        ctx.fill();
      });

      // Spawn new shooting stars more frequently
      if (time - lastShootingStarTime > shootingStarInterval) {
        shootingStarsRef.current.push(createShootingStar());
        lastShootingStarTime = time;
        shootingStarInterval = 2000 + Math.random() * 3000;
      }

      // Draw and update shooting stars
      shootingStarsRef.current = shootingStarsRef.current.filter((star) => {
        // Move shooting star
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;

        // Draw shooting star trail with purple tint
        const gradient = ctx.createLinearGradient(
          star.x - Math.cos(star.angle) * star.length,
          star.y - Math.sin(star.angle) * star.length,
          star.x,
          star.y
        );
        gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
        gradient.addColorStop(0.5, `rgba(167, 139, 250, ${star.opacity * 0.4})`);
        gradient.addColorStop(0.8, `rgba(196, 181, 253, ${star.opacity * 0.7})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${star.opacity})`);

        ctx.beginPath();
        ctx.moveTo(
          star.x - Math.cos(star.angle) * star.length,
          star.y - Math.sin(star.angle) * star.length
        );
        ctx.lineTo(star.x, star.y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw bright head with glow
        ctx.beginPath();
        ctx.arc(star.x, star.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = star.color;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Keep if still on screen
        return star.x < width + 50 && star.y < height + 50;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    const handleResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
      starsRef.current = createStars();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// Dashboard Hero Space Background - optimized for small rectangular card
const DashboardSpaceBackground = () => {
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        zIndex: 0,
        borderRadius: "inherit",
      }}
    >
      {/* Deep space gradient with purple tones */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, #0f0c1d 0%, #1a1339 25%, #2d1b69 50%, #1e1142 75%, #0f0c1d 100%)",
        }}
      />

      {/* Purple galaxy band glow */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: `linear-gradient(
            135deg,
            transparent 0%,
            transparent 30%,
            rgba(139, 92, 246, 0.08) 45%,
            rgba(167, 139, 250, 0.12) 50%,
            rgba(139, 92, 246, 0.08) 55%,
            transparent 70%,
            transparent 100%
          )`,
          filter: "blur(30px)",
          pointerEvents: "none",
        }}
      />

      {/* Ambient purple glows */}
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)",
          top: "-20%",
          left: "-10%",
          filter: "blur(40px)",
          animation: "ambientFloat1 20s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(124, 58, 237, 0.2) 0%, transparent 70%)",
          bottom: "-25%",
          right: "-5%",
          filter: "blur(50px)",
          animation: "ambientFloat2 25s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* Star field canvas */}
      <StarCanvas />

      {/* Subtle vignette */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(10, 6, 18, 0.4) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* CSS animations */}
      <style>
        {`
          @keyframes ambientFloat1 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
              opacity: 0.4;
            }
            50% {
              transform: translate(20px, 15px) scale(1.1);
              opacity: 0.6;
            }
          }
          @keyframes ambientFloat2 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
              opacity: 0.35;
            }
            50% {
              transform: translate(-15px, -10px) scale(1.05);
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DashboardSpaceBackground;
